import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
// v0.16.2 — 三个下拉从 Flarum 原生 `Select` 换回原生 `Dropdown`
//   (辉哥 10/10: "新建跟进 modal 里面的 dropdown, 就按照预约管理 tab 中的时间筛选 dropdown 那样就行")
//   模板照抄 LessonsTab.js:261 预约管理的时间筛选, 状态取值那一版抄 StatsTab.js:151 的时间范围:
//   <Dropdown buttonClassName="ZS-filter-dropdown-btn" icon label={当前值}> + <Button active>。
//   v0.16.1 短暂用过 Select (<select> 控件), 本轮换掉的理由见 CHANGELOG v0.16.2。
import Dropdown from 'flarum/common/components/Dropdown';
import Button from 'flarum/common/components/Button';
import Api from '../utils/Api';
import { mountDatePicker } from '../utils/DatePickerHelper';
// v0.16.7 — 「经办人」那套可搜索下拉整体抽成公用组件 (辉哥 10/11 拍板:
//   "可以把这个搜索下拉框做成一个通用方法, 这样在 scheduler 其他功能中也能复用")。
// 本 modal 现在用它渲染**两个**下拉: 经办人(role=coach,sales) + 教练(role=coach)。
// v0.16.6 在经办人上修好的回归点 (浮动定位 / 三条关闭时机 / 候选项能点中) 一并搬进组件。
import SearchableUserPicker from '../utils/SearchableUserPicker';
// v0.19.18 — 分区骨架抽成**共用入口** (本仓铁律「一个逻辑一个入口」):
//   「卡片详情」弹层要跟本 modal 用**同一套**分区排版, 骨架原本是本文件里的一个
//   类方法 ⇒ 两边各抄一份迟早分叉 ⇒ 收进 utils/FollowupForm.js, 两个弹层都 import。
//   ⚠️ 本次只改 import + 调用点, 本 modal 的 DOM 结构与样式**一字不动**。
import { renderSection } from '../utils/FollowupForm';
// v0.19.20 (批 41 · 段②) — 「字段图标」改用**共用入口**
//   (本仓铁律「一个逻辑一个入口, 不许抄两遍」; 改前这个原语在本文件里抄了一份)。
//   ⚠️ 上一行对 `utils/FollowupForm` 的 import **一个字都没改** (批 39 已上线的产物, 零扰动)。
import { fieldIcon, errReason } from '../utils/FormPrimitives';

/**
 * v0.16.1 — 跟进阶段。**value 是后端英文 key, 显示中文**, 两者不能互换:
 *   显示「已联系」但存进去的必须是 'contacted', 否则 DB 里 stage 就废了。
 *   全部非整数字符串 key。
 */
const FOLLOWUP_STAGES = {
  potential: '潜在',
  contacted: '已联系',
  booked: '已约课',
  trial: '已试课',
  closed: '已成交',
  no_intent: '无意向'
};

/**
 * v0.16.0 — 新建**客户**跟进 modal (辉哥 10/10 拍板 方案 C「分区卡片»)
 *
 * 跟进对象从「学员」改成「客户」:
 *   - 删掉了原来的「学员」选择框 (student_id 不再由前端提交, 客户可以完全不在册)
 *   - 新增客户信息 6 字段: 姓名 / 年龄 / 电话 / 课程分类 / 来源渠道 / 个人情况
 *   - 经办人从「后端默认 actor.id」改成**前端下拉指定**(可搜索单选)
 *
 * 设计稿: design/sales-followup/C-modal.html + C-modal.png (本文件按它 1:1 实现)
 *
 * ## ⚠️ 空值是空值, 不是 0 (v0.15.2 教训)
 *   后端已经踩过一次 `PATCH {"total_lessons": ""}` 把课时静默清成 0 的坑。
 *   这里所有 select/input 存的都是**字符串原样**, 提交时才做一次归一化
 *   (见 buildPayload): 空 → null, "0" → 0。绝不在输入阶段就 `+value`。
 *
 * ## 数据源 (全部走 API, 不硬编码)
 *   - 课程分类: GET /admin/lesson-options  → .categories
 *   - 来源渠道: GET /admin/lead-channels   → .channels
 *   - 经办人  : GET /users?role=coach,sales&school_id=N  (跟 BindCardModal L123 同款口径)
 *   - 教练    : GET /users?role=coach&school_id=N          (v0.16.7 新增, 同一套候选机制)
 *
 * ## v0.16.7 — 新增「教练」下拉 (与经办人同一行)
 *   候选只取后台设置 `scheduler-role-coach-group.group` 映射的 Flarum 用户组,
 *   即 `role=coach`。⚠️ RoleResolver 优先级是 **student > coach > sales**, 所以
 *   「既是学员又是教练」的人会被判成 student 而从 role=coach 里消失 —— 本地/生产
 *   实测该重叠为 **0 人** (教练组成员都不在学员组), 所以直接复用现有端点, 不另写查询。
 */
export default class SalesFollowupCreateModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.activeSchoolId = vnode.attrs.activeSchoolId;
    this.onCloseCb = vnode.attrs.onClose;
    this.saving = false;

    // v0.16.0 — 阶段默认 contacted (新建跟进 = 已经联系过了才建记录)
    this.form = {
      customer_name: '',
      age: '',
      phone: '',
      lesson_category_id: '',
      lead_channel_id: '',
      personal_note: '',
      sales_id: '',
      // v0.16.7 — 教练**选填** (Mavis 定的默认)。要改成必填, 这四处一起改:
      //   ① 下面 fields.coach_id 加 required: true;
      //   ② validate() 里加一条 if (!this.form.coach_id) return '请选择教练';
      //   ③ 后端 SalesFollowupValidator::coachId() 的 $required 默认值改成 true;
      //   ④ (可选) 迁移把 coach_id 收成 NOT NULL —— ⚠️ 会因存量 NULL 行失败,
      //      要先回填, 属于破坏性变更, 别顺手做。
      coach_id: '',
      stage: 'contacted',
      content: '',
      next_followup_at: ''
    };

    // v0.16.7 — 两个「可搜索用户单选」下拉共用一个组件实例, 状态按 key 独立。
    // v0.16.9 — 经办人**去掉预选当前登录人** (辉哥 10/11: 「新建跟进modal里面, 经办人搜索框
    //   那块现在有默认选择, 去掉吧」)。两个框一律留空, 由用户自己选。
    //   经办人仍然必填: 不选直接提交会看到「请选择经办人」, 那是预期行为不是 bug。
    //   教练选填, 本来就不预选。
    this.picker = new SearchableUserPicker({
      host: this,
      getSchoolId: () => this.activeSchoolId,
      getValue: key => this.form[key],
      // v0.18.84 — 选中也走**同一个**赋值入口 ⇒ 用户一选经办人, 那条「请选择经办人」立刻消失。
      setValue: (key, v) => {
        this.setField(key, v);
      },
      fields: {
        sales_id: {
          role: 'coach,sales,manager',
          label: '经办人',
          icon: 'fa-user-tie',
          required: true,
          groupClass: 'ZS-fg-agent'
        },
        coach_id: {
          role: 'coach',
          label: '教练',
          icon: 'fa-user-tie',
          hint: '选填',
          groupClass: 'ZS-fg-coach'
        }
      }
    });
    this.categories = [];
    this.channels = [];
    this.loading = true;
    this._fpTimer = null;
    this._fpInstance = null;

    // ⚠️ v0.18.84 (P2-7) —— 字段级错误 { 字段: 文案 }。
    //   改前提交不通过只有一个原生 alert(而且只报**第一条**错) ⇒ 生产实测
    //   (E2E 截图 s01-04): 留空必填项点「保存跟进」, 界面上**看不到任何提示**。
    //   改后: ① 每个字段下方一条红色文案 ② 弹层顶部一条汇总提示。
    //   ⛔ 校验规则一个字没改 (哪些必填 / 什么格式) —— 这里只存**反馈**。
    this.errors = {};

    // v0.16.5 — 电话查重状态机 (辉哥 10/11 拍板「电话要唯一, 遇到已存在电话时要给出提示」)
    //   idle      还没查 / 输入还没失焦 → 不显示任何东西
    //   checking  在途 → 显示「正在检查」, 绝不能显示成「无重复」
    //   ok        查过且无重复 → **不渲染任何东西** (提示不是常驻控件)
    //   duplicate 有重复 → 就地提示 (不是 alert, alert 会把用户从表单里踢出去)
    //   unknown   请求失败 → 中性灰字, **绝不冒充 ok** (见 checkPhone 的 catch)
    this.phoneCheck = {
      state: 'idle',
      message: null
    };
    // 单调递增的请求序号 —— 只有最后一次发起的查询才有资格写回结果。
    // 网络乱序时 (先发的慢、后发的快), 旧响应后到会覆盖新结果, 提示就变成错的那一条。
    this._phoneCheckSeq = 0;
    this._phoneCheckTimer = null;
    this.load();
  }
  className() {
    // 跟 AddStudentModal / BindCardModal 同款: desktop 居中 + mobile 全屏
    return 'Modal--medium Modal--fullscreen-padded ZS-SalesFollowupCreateModal';
  }
  title() {
    return m("span", null, m("i", {
      className: "fas fa-phone",
      style: "color:var(--zs-cyan-06b6d4, #06b6d4);margin-right:8px;"
    }), "\u65B0\u5EFA\u5BA2\u6237\u8DDF\u8FDB");
  }
  async load() {
    try {
      this.loading = true;
      m.redraw();
      // 两个候选源并发拉。分类/渠道走 admin 端点 (权限 scheduler.viewStats,
      // 跟本 controller 的权限闸门一致)。
      const [opts, chans] = await Promise.all([Api.get('admin/lesson-options').catch(() => ({
        categories: []
      })), Api.get('admin/lead-channels').catch(() => ({
        channels: []
      }))]);
      this.categories = opts && opts.categories || [];
      this.channels = chans && chans.channels || [];
      // v0.16.7 — 经办人 / 教练候选由可搜索下拉组件自己拉 (role=coach,sales / role=coach)。
      //   await 在 loading 态里, 免得弹窗先渲染出一个空下拉再突然冒出候选。
      await this.picker.loadAll();
    } catch (e) {
      console.error('[followup-create] load options failed', e);
    } finally {
      this.loading = false;
      m.redraw();
      this.tryInitDatePicker();
    }
  }

  // ── 日期选择 (复用公用 helper, v0.6.59 沉淀) ─────────────────────
  oncreate(vnode) {
    super.oncreate(vnode);
    this.tryInitDatePicker();
    // v0.16.7 — 可搜索下拉的「点外部关闭」监听搬到组件里 (mount/destroy 成对)。
    this.picker.mount();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.tryInitDatePicker();
    // v0.16.6 — 每轮 patch 后重新判定要不要向上翻 (下拉是这轮刚插进来的)。
    this.picker.afterUpdate();
  }
  onremove(vnode) {
    super.onremove(vnode);
    if (this._fpTimer) {
      clearTimeout(this._fpTimer);
      this._fpTimer = null;
    }
    // v0.16.5 — 查重防抖定时器也要清, 否则 modal 关掉后定时器照样触发 setPhoneCheck → m.redraw()
    if (this._phoneCheckTimer) {
      clearTimeout(this._phoneCheckTimer);
      this._phoneCheckTimer = null;
    }
    // v0.16.7 — document 监听由组件自己摘。不摘的话 modal 关掉后它还活着,
    // 每次全站 mousedown 都会跑进一个已死的组件实例 (this 也跟着泄漏)。
    this.picker.destroy();
    if (this._fpInstance) {
      this._fpInstance.destroy();
      this._fpInstance = null;
    }
  }
  async tryInitDatePicker() {
    if (this._fpInstance) return;
    const el = this.element && this.element.querySelector('.ZS-flatpickr-input');
    if (!el) {
      if (this.loading) return; // 还在加载, 加载完 finally 里会再调一次
      if (this._fpTimer) return;
      // 视图还没渲染出来时退避重试, 上限交给调用方 (v0.6.19 同套路)
      this._fpTimer = setTimeout(() => {
        this._fpTimer = null;
        this.tryInitDatePicker();
      }, 120);
      return;
    }
    this._fpInstance = await mountDatePicker(el, {
      // mode 是 'date' 不是 'day' (DatePickerHelper 只认 date/month/range)。
      // dateFormat 沿用本 modal v0.15.x 之前的 'Y-m-d', 别用 helper 默认的 'Y/m/d':
      //   列表页按 next_followup_at 前 10 位直接当日期显示, 带斜杠会串。
      mode: 'date',
      dateFormat: 'Y-m-d',
      onChange: (selectedDates, dateStr) => {
        this.form.next_followup_at = dateStr || '';
        m.redraw();
      }
    });
  }

  /**
   * ⚠️ v0.16.0 — 提交前的唯一归一化点。
   *   这里是全文件唯一允许做「字符串 → 类型」转换的地方, 输入阶段一律原样存字符串。
   *   · age: '' / undefined → **null** (不是 0!); '0' → 0 (婴幼儿是合法输入)
   *   · phone / 下拉 id / personal_note: 空 → null
   */
  buildPayload() {
    const age = String(this.form.age).trim();
    const phone = String(this.form.phone).trim();
    return {
      customer_name: String(this.form.customer_name).trim(),
      age: age === '' ? null : age,
      phone: phone === '' ? null : phone,
      lesson_category_id: this.form.lesson_category_id || null,
      lead_channel_id: this.form.lead_channel_id || null,
      personal_note: this.form.personal_note.trim() || null,
      sales_id: +this.form.sales_id,
      // v0.16.7 — 教练选填: 没选就是 null (不是 0)。同 sales_id 的口径, 归一化只在这一处发生。
      coach_id: this.form.coach_id ? +this.form.coach_id : null,
      stage: this.form.stage,
      content: String(this.form.content).trim(),
      next_followup_at: this.form.next_followup_at || null,
      school_id: +this.activeSchoolId
    };
  }

  /**
   * ⚠️ v0.18.84 — 提交前的**唯一**校验入口 (逐字段).
   *
   *   **规则一个字没改** (哪些必填 / 什么格式全部照旧), 这里只拆成「一个字段一个函数」,
   *   好让**同一个规则**既能在提交时全量跑一遍, 又能在用户改这个字段时**只重算这一个字段**。
   *
   * @param key  字段名 (customer_name / age / phone / sales_id / content / school)
   * @returns {string|null} 该字段当前的错误文案; null = 没问题
   */
  validateField(key) {
    const T = k => app.translator.trans('ziven-scheduler.forum.sales.followup-form.' + k);
    if (key === 'customer_name') {
      const name = String(this.form.customer_name).trim();
      if (!name) return T('err-customer-name-required');
      if (name.length > 64) return T('err-customer-name-too-long');
      return null;
    }
    if (key === 'age') {
      const age = String(this.form.age).trim();
      if (age === '') return null; // 选填
      // v0.16.10 — 支持 1 位小数 (辉哥: 7.5 岁的小孩)。判定口径跟后端
      //   SalesFollowupValidator::age() 一致 (同样的 1e-9 容差), 免得出现
      //   「前端放行、后端 422」这种两边不一致。
      const a = Number(age);
      if (!isFinite(a)) return T('err-age-not-number');
      if (Math.abs(a - Math.round(a * 10) / 10) > 1e-9) return T('err-age-decimals');
      if (a < 0 || a > 120) return T('err-age-range');
      return null;
    }
    if (key === 'phone') {
      const phone = String(this.form.phone).trim();
      // v0.16.5 — 电话必填 (辉哥 10/11 拍板)。空值单列一条文案, **不混进格式错**:
      //   「没填」和「填错了」要让用户知道下一步分别该干什么 (去填 / 去改)。
      if (phone === '') return T('err-phone-required');
      if (!/^1[3-9]\d{9}$/.test(phone)) return T('err-phone-invalid');
      return null;
    }
    if (key === 'sales_id') return this.form.sales_id ? null : T('err-agent-required');
    // v0.16.7 — 教练**选填**, 所以这里**不**加校验。要改成必填就加这一行:
    //   if (key === 'coach_id' && !this.form.coach_id) return T('err-coach-required');
    if (key === 'content') return String(this.form.content).trim() ? null : T('err-content-required');
    if (key === 'school') return this.activeSchoolId ? null : T('err-school-required');
    return null;
  }

  /**
   * v0.18.84 — 提交前的**唯一**校验入口 (全量).
   *
   *   **改前**: 返回**第一条**错误字符串, 由 submit() 弹一个原生 `alert()`。
   *   **改后**: 返回 `{ 字段: 文案 }` 的**全部**错误 (规则一字未改, 只是**全报**),
   *            让用户一次看清**到底缺哪几项**。
   */
  validate() {
    const errors = {};
    ['customer_name', 'age', 'phone', 'sales_id', 'content', 'school'].forEach(key => {
      const msg = this.validateField(key);
      if (msg) errors[key] = msg;
    });
    return errors;
  }

  /**
   * ⚠️ v0.18.84 — **所有输入框的唯一赋值入口**。
   *
   *   ① 改到哪个字段, 就**只重算那个字段**的错误:
   *      · 填好了 ⇒ 那条红字消失;
   *      · 还没填 ⇒ 红字留着;
   *      · 填了但格式不对 ⇒ 红字**换**成对应的那一条 (例: 手机号 138 → 13812345678 ⇒ 格式错那条消失);
   *      ⛔ **不动别的字段** —— 用户只填了姓名, 手机号还是空的 ⇒ 手机号那条红字必须还在。
   *   ② 「经办人」那个字段的错误条渲染在**可搜索下拉组件自己的槽位**里 ⇒ 一并同步。
   */
  setField(key, value) {
    this.form[key] = value;
    if (this.errors) {
      const msg = this.validateField(key);
      if (msg) {
        this.errors[key] = msg;
      } else {
        delete this.errors[key];
      }
      if (this.picker && this.picker.fields && this.picker.fields[key]) {
        this.picker.fields[key].error = msg || null;
      }
    }
    // ⚠️ `m` 是全局 (webpack 解析不到 'mithril' 那个包名) ⇒ 兜底判一下,
    //    写法跟 js/src/forum/utils/SearchableUserPicker.js:151 同款。
    if (typeof m !== 'undefined') m.redraw();
  }

  /**
   * v0.18.84 (P2-7) —— 字段下方的**红色**错误文案。
   *
   * ⚠️ 复用 `.ZS-phone-hint.duplicate` —— 本 modal 里**已经有**的「字段下方红色提示」
   *   样式 (作用域锁死 `.Modal.ZS-SalesFollowupCreateModal`), ⛔ 不另造第二套错误样式。
   */
  renderFieldError(key) {
    const msg = this.errors && this.errors[key];
    if (!msg) return null;
    return m("div", {
      className: "ZS-phone-hint duplicate"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), " ", msg);
  }

  /**
   * v0.18.84 (P2-7) —— 顶部**汇总**提示 (字段下方红字的索引)。
   * ⛔ 不许自动提交、不许清空用户已填的内容 —— 提示只是提示。
   */
  renderErrorSummary() {
    if (!this.errors || !Object.keys(this.errors).length) return null;
    return m("div", {
      className: "ZS-newversion-error"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), ' ', app.translator.trans('ziven-scheduler.forum.sales.followup-form.err-summary'));
  }

  // ── v0.16.5 电话查重 (失焦触发) ─────────────────────────────────
  //
  // ⚠️ 三处防御缺一不可, 都是实测会咬人的:
  //   防抖      失焦常常紧跟着「粘贴完就点下一个框」, 不防抖就是两次请求;
  //   序号作废  输入一改, 在途请求的结论立刻失效 (否则慢响应回来覆盖新输入的提示);
  //   失败中性  请求失败**不能**当成「无重复」, 那等于在失败时给销售一个假的安全信号。

  /** 输入中: 作废在途查询 + 清除旧提示 */
  onPhoneInput(e) {
    this.setField('phone', e.target.value);
    this._phoneCheckSeq += 1;
    if (this._phoneCheckTimer) {
      clearTimeout(this._phoneCheckTimer);
      this._phoneCheckTimer = null;
    }
    if (this.phoneCheck.state !== 'idle') this.setPhoneCheck('idle', null);
  }

  /** 失焦: 防抖 300ms 后再查, 避免连点几个框连打几次请求 */
  onPhoneBlur() {
    if (this._phoneCheckTimer) {
      clearTimeout(this._phoneCheckTimer);
      this._phoneCheckTimer = null;
    }
    this._phoneCheckTimer = setTimeout(() => {
      this._phoneCheckTimer = null;
      this.checkPhone();
    }, 300);
  }
  setPhoneCheck(state, message) {
    this.phoneCheck = {
      state,
      message: message || null
    };
    m.redraw();
  }
  async checkPhone() {
    const phone = String(this.form.phone).trim();
    const schoolId = +this.activeSchoolId;
    // 本次查询的"身份证": 回来时序号对不上就说明期间又发起/作废过查询, 直接丢弃。
    const seq = ++this._phoneCheckSeq;

    // 格式还没对 / 校区没选 → 不查。这里**不**冒充"无重复" (idle 什么都不显示),
    // 「必填」和「格式错」两种错由 validate() 单独报, 不跟查重提示混在一起。
    if (!schoolId || !/^1[3-9]\d{9}$/.test(phone)) {
      this.setPhoneCheck('idle', null);
      return;
    }
    this.setPhoneCheck('checking', null);
    try {
      const r = await Api.get('sales-followups/check-phone', {
        phone,
        school_id: schoolId
      });
      if (seq !== this._phoneCheckSeq) return; // 旧请求后到 → 丢弃
      if (!r || r.valid !== true) {
        this.setPhoneCheck('idle', null);
        return;
      }
      if (r.duplicate) {
        this.setPhoneCheck('duplicate', r.message);
      } else {
        this.setPhoneCheck('ok', null); // 无重复 → 清除提示 (ok 不渲染)
      }
    } catch (e) {
      if (seq !== this._phoneCheckSeq) return;
      console.warn('[followup-create] phone check failed', e);
      this.setPhoneCheck('unknown', null); // 失败 = 中性, 不拦提交 (真校验在服务端)
    }
  }

  /**
   * 电话下方的查重提示。**就地显示, 不用 alert**:
   *   alert 会把用户从表单里踢出去, 关掉之后还要重新找光标落在哪。
   */
  renderPhoneCheck() {
    const st = this.phoneCheck.state;
    if (st === 'checking') {
      return m("div", {
        className: "ZS-phone-hint checking"
      }, m("i", {
        className: "fas fa-circle-notch fa-spin"
      }), " \u6B63\u5728\u68C0\u67E5\u8BE5\u7535\u8BDD\u662F\u5426\u91CD\u590D\u2026");
    }
    if (st === 'unknown') {
      return m("div", {
        className: "ZS-phone-hint unknown"
      }, m("i", {
        className: "fas fa-circle-info"
      }), " \u7535\u8BDD\u67E5\u91CD\u6682\u4E0D\u53EF\u7528, \u63D0\u4EA4\u65F6\u7531\u670D\u52A1\u5668\u6821\u9A8C");
    }
    if (st === 'duplicate') {
      return m("div", {
        className: "ZS-phone-hint duplicate"
      }, m("i", {
        className: "fas fa-triangle-exclamation"
      }), " ", this.phoneCheck.message);
    }
    return null; // idle / ok: 无重复就什么都不显示
  }
  async submit() {
    // ⚠️ v0.18.84 (P2-7) —— 校验不过 ⇒ **不发任何请求**, 只把错误显示出来。
    //   改前这里是 `alert(err)` ⇒ 生产实测: 留空必填项点「保存跟进」, 界面上毫无提示。
    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.errors = errors;
      // 「经办人」那个字段是 SearchableUserPicker 渲染的 ⇒ 把文案塞进它的 config
      //   (picker 支持可选的 `error`, 没传就不渲染 ⇒ 别的 modal 一行 DOM 都不变)。
      if (this.picker && this.picker.fields && this.picker.fields.sales_id) {
        this.picker.fields.sales_id.error = errors.sales_id || null;
      }
      m.redraw();
      return; // ⛔ 不提交、不清空用户已填的内容
    }
    this.errors = {};
    if (this.picker && this.picker.fields && this.picker.fields.sales_id) {
      this.picker.fields.sales_id.error = null;
    }
    try {
      this.saving = true;
      m.redraw();
      const payload = this.buildPayload();
      const r = await Api.create('sales-followups', payload);
      this.saving = false;
      if (this.onCloseCb) this.onCloseCb(r);
      this.hide();
    } catch (e) {
      this.saving = false;
      m.redraw();
      const msg = errReason(e, '保存失败');
      console.error('[followup-create] save failed', e);
      alert(msg);
    }
  }

  // ── 小组件 ──────────────────────────────────────────────────────
  //  ⚠️ v0.19.20 (批 41 · 段②) — `fieldIcon` 的实现**搬**进共用入口 `utils/FormPrimitives.js`
  //    (改前它在本文件里抄了一份 ⇒ 本仓铁律「一个逻辑一个入口」)
  //    ⇒ 本文件的 DOM 结构与样式**一字不动**。

  /**
   * v0.16.2 — 三个下拉的候选集: `{value, label}` 数组。
   *
   * ⚠️ `value` 必须还是**后端 id 原样** ('builtin-park' / 雪花 id / 'trial'),
   *    中文只是 `label`。`Dropdown` 是菜单不是表单控件, 它自己不认识「当前值」,
   *    value↔label 的对应关系只能靠这个数组维持 —— 拿 label 当 value 提交,
   *    DB 里就再也认不出这个分类了 (同 buildPayload 的空值教训, 一个性质)。
   *
   * 用数组 (而不是 `Select` 时代的 {value: label} 对象) 的另一个好处:
   *   `Select` 内部靠 `Object.keys` 渲染, 整数字符串 key 会被 JS 排到前面
   *   (管理员一旦加 numeric 分类, 它就会插到占位项前面); 现在顺序 = 后端返回顺序。
   */
  categoryOptions() {
    return this.categories.filter(c => c.enabled).map(c => ({
      value: String(c.id),
      label: c.name
    }));
  }
  channelOptions() {
    return this.channels.filter(c => c.enabled).map(c => ({
      value: String(c.id),
      label: c.name
    }));
  }
  stageOptions() {
    return Object.keys(FOLLOWUP_STAGES).map(k => ({
      value: k,
      label: FOLLOWUP_STAGES[k]
    }));
  }

  /**
   * v0.16.2 — 点菜单项: 只把 `option.value` (id) 写回表单 + 重绘。
   *   菜单收起由 Bootstrap 自己完成 (点 .Dropdown-menu 内的元素自动关)。
   *   这里是**唯一**允许改 form.xxx 下拉字段的地方, label 一个都不碰。
   */
  chooseDropdown(formKey, value) {
    this.setField(formKey, value);
  }

  /**
   * v0.16.2 — 把 Flarum 原生 `Dropdown` 当表单控件用的统一渲染。
   *
   * `Dropdown` 没有「当前选中值」的概念 (v0.16.1 的 `Select` 是 <select> 才有),
   * 所以下面三件事必须手动补, 少一件就会「界面变了但提交的还是旧值」:
   *   1. label = 当前选中项的文字, 没选时显示 placeholder
   *   2. 当前项的 `Button` 打 active
   *   3. onSelect 只收 option.value (id), 不碰 label
   *
   * `lazyDraw={true}` 跟 OperationLogsTab.js:290 同款 (v0.6.62b 踩过):
   *   Dropdown 在 `shown.bs.dropdown` 里会 m.redraw(), 异步重渲染会把菜单 className
   *   写回 'Dropdown-menu dropdown-menu ...', 抹掉 Bootstrap 的 `.show`, 菜单再也不显示。
   */
  renderChoiceDropdown(opts) {
    const current = opts.options.find(o => o.value === String(opts.value));
    // ⚠️ children 必须是**一个**数组 vnode, 不能写成 `{cond && x}{items.map(...)}` 两个子节点。
    //    mithril 的 m() 只有在子节点恰好 1 个时才把 `children[0]` 直接当 children
    //    (children.length===1 ? children[0] : children); 两个子节点时会把整个数组当成
    //    **一个** vnode, Dropdown 内部的 listItems 就只包出一个 <li>, N 个按钮全挤在里面
    //    —— core CSS 是 `.Dropdown-menu > li > button` (直接子选择器), 结构错了样式全废。
    //    跟 AbstractPost.tsx:73 的 `{controls}` 同款写法。
    const items = opts.options.length ? opts.options.map(o => m(Button, {
      key: o.value,
      active: !!current && current.value === o.value,
      onclick: () => this.chooseDropdown(opts.formKey, o.value)
    }, o.label)) : [m(Button, {
      key: "__empty",
      disabled: true
    }, "\u6682\u65E0\u53EF\u9009\u9879")];
    return m(Dropdown, {
      className: "ZS-followup-dropdown",
      buttonClassName: "ZS-filter-dropdown-btn ZS-followup-dropdown-btn",
      menuClassName: "ZS-followup-dropdown-menu",
      icon: opts.icon,
      label: current ? current.label : opts.placeholder,
      lazyDraw: true
    }, items);
  }
  renderCustomerSection() {
    return renderSection(1, '客户信息', '这条记录「关于谁」', [
    // 姓名* | 年龄
    m("div", {
      className: "ZS-form-row"
    }, m("div", {
      className: "ZS-form-group ZS-fg-name"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-user'), " \u5BA2\u6237\u59D3\u540D ", m("span", {
      className: "req"
    }, "*")), m("input", {
      className: "ZS-form-input",
      placeholder: "\u4F8B: \u738B\u5C0F\u96E8",
      value: this.form.customer_name,
      oninput: e => this.setField('customer_name', e.target.value)
    }), this.renderFieldError('customer_name')), m("div", {
      className: "ZS-form-group ZS-fg-age"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-calendar-day'), " \u5E74\u9F84"), m("input", {
      className: "ZS-form-input",
      type: "number",
      min: "0",
      max: "120"
      // v0.16.10 — step 必须是 0.1。**不加这个, 浏览器原生 stepper 和表单校验
      //   只认整数**, 上下箭头会一格一格跳 1, 手输 7.5 还会被浏览器标成非法值。
      ,
      step: "0.1",
      placeholder: "\u4F8B: 34 \u6216 7.5",
      value: this.form.age,
      oninput: e => {
        // ⚠️ 原样存字符串, 不在这里 +value:
        //   空串必须一路保持空串直到 buildPayload 才变 null, 中途变 0 就完了
        this.setField('age', e.target.value);
      }
    }), this.renderFieldError('age'))),
    // 电话 | 课程分类
    m("div", {
      className: "ZS-form-row"
    }, m("div", {
      className: "ZS-form-group ZS-fg-phone"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-phone'), " \u7535\u8BDD ", m("span", {
      className: "req"
    }, "*")), m("input", {
      className: "ZS-form-input",
      placeholder: "\u4F8B: 13800138000",
      value: this.form.phone,
      oninput: e => this.onPhoneInput(e),
      onblur: () => this.onPhoneBlur()
    }), this.renderPhoneCheck(), this.renderFieldError('phone')), m("div", {
      className: "ZS-form-group ZS-fg-category"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-tag'), " \u8BFE\u7A0B\u5206\u7C7B"), this.renderChoiceDropdown({
      icon: 'fas fa-tag',
      options: this.categoryOptions(),
      value: this.form.lesson_category_id,
      placeholder: '— 选择课程分类 —',
      formKey: 'lesson_category_id'
    }))),
    // 来源渠道 (v0.16.7 — 单独一行。桌面是 2 列 grid, 独占一格留白;
    // 目的只是让下面「经办人 | 教练」能真正并排 —— 辉哥 10/11 要求两者同一行)
    m("div", {
      className: "ZS-form-row"
    }, m("div", {
      className: "ZS-form-group ZS-fg-channel"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-bullhorn'), " \u6765\u6E90\u6E20\u9053"), this.renderChoiceDropdown({
      icon: 'fas fa-bullhorn',
      options: this.channelOptions(),
      value: this.form.lead_channel_id,
      placeholder: '— 选择来源渠道 —',
      formKey: 'lead_channel_id'
    }))),
    // 经办人 | 教练 (v0.16.7 — 两个可搜索单选并排, 同一个 SearchableUserPicker 实例)
    m("div", {
      className: "ZS-form-row"
    }, this.picker.render('sales_id'), this.picker.render('coach_id')),
    // 个人情况 (通栏)
    m("div", {
      className: "ZS-form-row full"
    }, m("div", {
      className: "ZS-form-group ZS-fg-note"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-file-lines'), " \u4E2A\u4EBA\u60C5\u51B5", m("span", {
      className: "hint"
    }, "\u5BA2\u6237\u80CC\u666F / \u8EAB\u4F53\u72B6\u51B5 / \u7279\u6B8A\u8981\u6C42")), m("textarea", {
      className: "ZS-form-input",
      rows: "2",
      placeholder: "\u4F8B: \u6210\u4EBA, \u96F6\u57FA\u7840\u6ED1\u51B0, \u6015\u51B7, \u819D\u76D6\u6709\u65E7\u4F24",
      value: this.form.personal_note,
      oninput: e => this.setField('personal_note', e.target.value)
    })))]);
  }
  renderFollowupSection() {
    return renderSection(2, '跟进信息', '这次「聊了什么、聊到哪一步」', [
    // 跟进内容* (通栏)
    m("div", {
      className: "ZS-form-row full"
    }, m("div", {
      className: "ZS-form-group ZS-fg-content"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-comment-dots'), " \u8DDF\u8FDB\u5185\u5BB9 ", m("span", {
      className: "req"
    }, "*")), m("textarea", {
      className: "ZS-form-input",
      rows: "2",
      placeholder: "\u4F8B: \u7535\u8BDD\u6C9F\u901A, \u5BA2\u6237\u5468\u516D\u4E0A\u5348\u6709\u7A7A, \u5DF2\u7EA6\u5468\u65E5\u4E0B\u5348\u8BD5\u8BFE",
      value: this.form.content,
      oninput: e => this.setField('content', e.target.value)
    }), this.renderFieldError('content'))),
    // 阶段 | 下次跟进时间
    m("div", {
      className: "ZS-form-row"
    }, m("div", {
      className: "ZS-form-group ZS-fg-stage"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-flag'), " \u9636\u6BB5"), this.renderChoiceDropdown({
      icon: 'fas fa-flag',
      options: this.stageOptions(),
      value: this.form.stage,
      placeholder: '— 选择阶段 —',
      formKey: 'stage'
    })), m("div", {
      className: "ZS-form-group ZS-fg-next"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-calendar-days'), " \u4E0B\u6B21\u8DDF\u8FDB\u65F6\u95F4", m("span", {
      className: "hint"
    }, "\u9009\u586B")), m("input", {
      className: "ZS-form-input ZS-flatpickr-input",
      placeholder: "\u9009\u62E9\u4E0B\u6B21\u8DDF\u8FDB\u65E5\u671F"
    })))]);
  }
  content() {
    return m("div", null, m("div", {
      className: "Modal-body"
    }, this.renderErrorSummary(), this.loading && m("div", {
      style: "padding:30px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u52A0\u8F7D\u9009\u9879\u4E2D..."), !this.loading && m("div", {
      className: "ZS-modal-form ZS-followup-form"
    }, this.renderCustomerSection(), this.renderFollowupSection())), m("div", {
      className: "Modal-footer"
    }, m("button", {
      className: "Button",
      onclick: () => this.hide()
    }, m("i", {
      className: "fas fa-xmark"
    }), " \u53D6\u6D88"), m("button", {
      className: "Button Button--primary",
      disabled: this.saving || this.loading,
      onclick: () => this.submit()
    }, m("i", {
      className: "fas fa-check"
    }), " ", this.saving ? '保存中...' : '保存跟进')));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/SalesFollowupCreateModal', SalesFollowupCreateModal);